(() => {
  'use strict';

  const doc = document;
  const body = doc.body;
  const isGerman = (doc.documentElement.lang || '').toLowerCase().startsWith('de');
  const isFrench = (doc.documentElement.lang || '').toLowerCase().startsWith('fr');
  const isEnglish = !(doc.documentElement.lang || '').toLowerCase().startsWith('nl');
  const storeOpen = body?.dataset.storeOpen === 'true';
  const launchLabel = body?.dataset.launchLabel || (isEnglish ? 'Temporarily unavailable' : 'Tijdelijk niet beschikbaar');
  const shippingThreshold = Number(body?.dataset.freeShippingCents || 4500);
  const shopRoot = window.Shopify?.routes?.root || '/';
  const localRoute = (path) => `${shopRoot}${String(path || '').replace(/^\/+/, '')}`.replace(/\/{2,}/g, '/');
  const text = (nl, en) => ((isFrench || isGerman) ? window.DailyBearI18n.translate(en) : isEnglish ? en : nl);
  const money = (cents) => new Intl.NumberFormat(isGerman ? 'de-DE' : isFrench ? 'fr-BE' : isEnglish ? 'en-BE' : 'nl-BE', { style: 'currency', currency: 'EUR' }).format((Number(cents) || 0) / 100);
  const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));

  function setScrollLock(locked) {
    body?.classList.toggle('no-scroll', Boolean(locked));
  }

  function initAnnouncement() {
    doc.querySelectorAll('[data-announcement]').forEach((bar) => {
      const items = Array.from(bar.querySelectorAll('.announcement__item'));
      if (items.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      let index = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
      window.setInterval(() => {
        items[index].classList.remove('is-active');
        index = (index + 1) % items.length;
        items[index].classList.add('is-active');
      }, 5000);
    });
  }

  function initMobileMenu() {
    const drawer = doc.querySelector('[data-mobile-drawer]');
    const openButton = doc.querySelector('[data-mobile-menu-open]');
    if (!drawer || !openButton) return;
    const close = () => {
      drawer.classList.remove('is-open');
      openButton.setAttribute('aria-expanded', 'false');
      setScrollLock(false);
    };
    openButton.addEventListener('click', () => {
      drawer.classList.add('is-open');
      openButton.setAttribute('aria-expanded', 'true');
      setScrollLock(true);
      drawer.querySelector('a, button, select')?.focus();
    });
    drawer.querySelector('[data-mobile-menu-close]')?.addEventListener('click', close);
    drawer.querySelectorAll('a').forEach((link) => link.addEventListener('click', close));
    doc.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
  }

  function initTabs() {
    doc.querySelectorAll('[data-tabs]').forEach((tabs) => {
      const buttons = Array.from(tabs.querySelectorAll('[data-tab-button]'));
      const panels = Array.from(tabs.querySelectorAll('[data-tab-panel]'));
      buttons.forEach((button) => button.addEventListener('click', () => {
        const id = button.dataset.tabButton;
        buttons.forEach((item) => item.classList.toggle('is-active', item === button));
        panels.forEach((panel) => panel.classList.toggle('is-active', panel.dataset.tabPanel === id));
      }));
    });
  }

  function initImageModal() {
    const triggers = doc.querySelectorAll('[data-image-modal-trigger]');
    if (!triggers.length) return;
    const modal = doc.createElement('div');
    modal.className = 'image-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = '<div class="image-modal__overlay" data-image-modal-close></div><div class="image-modal__panel" role="dialog" aria-modal="true"><button class="image-modal__close" type="button" aria-label="' + text('Sluiten', 'Close') + '" data-image-modal-close>×</button><img alt=""></div>';
    body.appendChild(modal);
    const image = modal.querySelector('img');
    const close = () => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      setScrollLock(false);
    };
    triggers.forEach((trigger) => trigger.addEventListener('click', () => {
      const source = trigger.querySelector('img') || trigger;
      image.src = source.currentSrc || source.src;
      image.alt = source.alt || '';
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      setScrollLock(true);
      modal.querySelector('button')?.focus();
    }));
    modal.querySelectorAll('[data-image-modal-close]').forEach((button) => button.addEventListener('click', close));
    doc.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal.classList.contains('is-open')) close();
    });
  }

  function initLaunchPopup() {
    const popup = doc.querySelector('[data-launch-popup]');
    if (!popup || storeOpen) return;
    const key = 'dailybear-launch-popup-dismissed';
    const close = () => {
      popup.hidden = true;
      setScrollLock(false);
      try { window.sessionStorage.setItem(key, 'true'); } catch (_error) { /* storage can be unavailable */ }
    };
    popup.querySelectorAll('[data-launch-popup-close]').forEach((button) => button.addEventListener('click', close));
    let dismissed = false;
    try { dismissed = window.sessionStorage.getItem(key) === 'true'; } catch (_error) { dismissed = false; }
    if (!dismissed) window.setTimeout(() => {
      const cookieBanner = doc.querySelector('[data-cookie-banner]');
      if (cookieBanner && !cookieBanner.hidden) return;
      popup.hidden = false;
      setScrollLock(true);
      popup.querySelector('button, input')?.focus();
    }, 6000);
  }

  function normalizeFlavor(value) {
    const normalized = String(value || '').toLowerCase();
    if (normalized.includes('blueberry') || normalized.includes('bosbes')) return 'blueberry';
    return 'strawberry';
  }

  function initProductVariant() {
    const root = doc.querySelector('[data-product-root]');
    if (!root) return;
    const select = root.querySelector('[data-variant-select]');
    const mainImage = root.querySelector('[data-gallery-main]');
    const thumbs = Array.from(root.querySelectorAll('[data-gallery-thumb]'));
    const title = root.querySelector('[data-product-title]');
    const label = root.querySelector('[data-product-flavor-label]');
    const prices = root.querySelectorAll('[data-db-price]');
    const panels = Array.from(root.querySelectorAll('[data-product-flavor-panel]'));

    const syncGalleryFallback = (flavor) => {
      const flavorThumbs = thumbs.filter((item) => normalizeFlavor(item.dataset.flavor) === flavor);
      const primaryThumb = flavorThumbs[0] || thumbs[0] || null;

      thumbs.forEach((item) => {
        const matchesFlavor = normalizeFlavor(item.dataset.flavor) === flavor;
        const active = item === primaryThumb;
        item.hidden = !matchesFlavor;
        item.setAttribute('aria-hidden', String(!matchesFlavor));
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });

      if (primaryThumb && mainImage) {
        mainImage.removeAttribute('srcset');
        mainImage.src = primaryThumb.dataset.src;
        mainImage.alt = primaryThumb.dataset.alt || '';
      }
    };

    const syncGallery = (flavor) => {
      root.dataset.currentFlavor = flavor;

      if (root.dataset.dailyBearGalleryReady === 'true') {
        root.dispatchEvent(new CustomEvent('dailybear:flavor-change', {
          detail: { flavor }
        }));
      } else {
        syncGalleryFallback(flavor);
      }
    };

    const applyFlavor = (requestedFlavor, updateUrl = true) => {
      const flavor = normalizeFlavor(requestedFlavor);
      let selectedOption = null;

      if (select) {
        selectedOption = Array.from(select.options).find((item) => item.dataset.flavor === flavor) || null;
        if (selectedOption) {
          select.value = selectedOption.value;
          const cents = Number(selectedOption.dataset.priceCents || 0);
          const compare = Number(selectedOption.dataset.compareCents || 0);
          const onSale = compare > cents;
          prices.forEach((price) => {
            price.querySelector('[data-price-current]').textContent = selectedOption.dataset.price;
            const previous = price.querySelector('[data-price-compare]');
            previous.textContent = selectedOption.dataset.compare || '';
            previous.hidden = !onSale;
            const badge = price.querySelector('[data-price-sale]');
            if (badge) {
              badge.hidden = !onSale;
              const percent = badge.querySelector('[data-sale-percent]');
              if (percent) percent.textContent = onSale ? `${Math.round((compare - cents) / compare * 100)}%` : '';
            }
          });
          root.querySelectorAll('.db-halloween-quantity [data-bundle-qty]').forEach((choice) => {
            const quantity = Number(choice.dataset.bundleQty);
            const unit = quantity >= 3 ? 2199 : cents;
            const total = unit * quantity;
            choice.querySelector('[data-bundle-unit]').textContent = money(unit);
            const previous = choice.querySelector('[data-bundle-compare]');
            if (previous) { previous.textContent = selectedOption.dataset.compare || ''; previous.hidden = !onSale; }
            choice.querySelector('[data-bundle-total]').textContent = money(total);
            choice.querySelector('[data-bundle-shipping]').hidden = total < shippingThreshold;
          });
          if (storeOpen) {
            const available = selectedOption.dataset.available === 'true';
            root.querySelectorAll('.db-atc-button, .mobile-sticky-atc button').forEach((button) => {
              button.disabled = !available;
              button.textContent = available ? (button.closest('.mobile-sticky-atc') ? text('In winkelmand', 'Add to cart') : text('Toevoegen aan winkelmand', 'Add to cart')) : text('Uitverkocht', 'Sold out');
            });
            const unavailableNote = root.querySelector('[data-product-unavailable]');
            if (unavailableNote) unavailableNote.hidden = available;
            root.querySelectorAll('[data-product-availability-label]').forEach((node) => { node.textContent = available ? text('Beschikbaar', 'Available') : text('Uitverkocht', 'Sold out'); });
          }
        }
      }
      if (title) title.textContent = title.dataset[`title${flavor[0].toUpperCase()}${flavor.slice(1)}`] || title.textContent;
      if (label) label.textContent = flavor === 'blueberry' ? text('Bosbes', 'Blueberry') : text('Aardbei', 'Strawberry');
      panels.forEach((panel) => { panel.hidden = panel.dataset.productFlavorPanel !== flavor; });
      syncGallery(flavor);

      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('smaak', flavor);
        if (selectedOption) url.searchParams.set('variant', selectedOption.value);
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => {
      const requestedFlavor = normalizeFlavor(thumb.dataset.flavor);
      const currentFlavor = normalizeFlavor(root.dataset.currentFlavor);
      if (requestedFlavor !== currentFlavor) applyFlavor(requestedFlavor);
    }));

    select?.addEventListener('change', () => applyFlavor(select.selectedOptions[0]?.dataset.flavor));
    const requested = new URLSearchParams(window.location.search).get('smaak');
    applyFlavor(requested || select?.selectedOptions[0]?.dataset.flavor || root.dataset.initialFlavor, Boolean(requested));
  }

  const cartDrawer = doc.querySelector('[data-db-cart-drawer]');
  const cartItems = doc.querySelector('[data-db-cart-items]');

  function openCartDrawer() {
    if (!cartDrawer) {
      window.location.href = localRoute('cart');
      return;
    }
    cartDrawer.classList.add('is-open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    setScrollLock(true);
    refreshCart();
    cartDrawer.querySelector('[data-db-cart-close]')?.focus();
  }

  function closeCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    setScrollLock(false);
  }

  function renderCart(cart) {
    doc.querySelectorAll('.cart-count, [data-db-cart-count]').forEach((counter) => { counter.textContent = cart.item_count; });
    if (cartItems) {
      if (!cart.items.length) {
        cartItems.innerHTML = `<div class="db-cart-drawer__empty">${text('Je winkelmand is nog leeg.', 'Your cart is empty.')}</div>`;
      } else {
        cartItems.innerHTML = cart.items.map((item, index) => {
          const image = item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" width="80" height="80">` : '';
          const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<small>${escapeHtml(item.variant_title)}</small>` : '';
          return `<article class="db-cart-drawer__item">${image}<div><strong>${escapeHtml(item.product_title)}</strong>${variant}<div class="db-cart-drawer__qty"><button type="button" data-cart-line-change="${index + 1}" data-cart-quantity="${Math.max(0, item.quantity - 1)}" aria-label="${text('Aantal verminderen', 'Decrease quantity')}">−</button><span>${item.quantity}</span><button type="button" data-cart-line-change="${index + 1}" data-cart-quantity="${item.quantity + 1}" aria-label="${text('Aantal verhogen', 'Increase quantity')}">+</button></div></div><div><strong>${money(item.final_line_price)}</strong><button type="button" class="db-cart-drawer__remove" data-cart-line-change="${index + 1}" data-cart-quantity="0">${text('Verwijderen', 'Remove')}</button></div></article>`;
        }).join('');
      }
    }
    doc.querySelectorAll('[data-db-cart-total], [data-db-cart-total-button]').forEach((node) => { node.textContent = money(cart.total_price); });
    const discountRow = doc.querySelector('[data-db-cart-discount-row]');
    const totalDiscount = Number(cart.total_discount || 0);
    if (discountRow) discountRow.hidden = totalDiscount <= 0;
    doc.querySelector('[data-db-cart-discount]')?.replaceChildren(doc.createTextNode(`-${money(totalDiscount)}`));
    const remaining = Math.max(0, shippingThreshold - cart.total_price);
    const shippingText = doc.querySelector('[data-db-shipping-text]');
    if (shippingText) shippingText.textContent = remaining > 0 ? text(`Nog ${money(remaining)} tot gratis verzending`, `${money(remaining)} left for free shipping`) : text('Je bestelling komt in aanmerking voor gratis verzending.', 'Your order qualifies for free shipping.');
    const bar = doc.querySelector('[data-db-shipping-bar]');
    if (bar) bar.style.width = `${shippingThreshold > 0 ? Math.min(100, (cart.total_price / shippingThreshold) * 100) : 100}%`;
  }

  async function refreshCart() {
    try {
      const response = await fetch(localRoute('cart.js'), { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!response.ok) throw new Error('cart');
      const cart = await response.json();
      renderCart(cart);
      return cart;
    } catch (_error) {
      if (cartItems) cartItems.innerHTML = `<p>${text('De winkelmand kon niet worden geladen. Probeer opnieuw.', 'The cart could not be loaded. Please try again.')}</p>`;
      return null;
    }
  }

  async function changeCartLine(line, quantity) {
    try {
      const response = await fetch(localRoute('cart/change.js'), { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity }) });
      if (!response.ok) throw new Error('change');
      renderCart(await response.json());
    } catch (_error) {
      window.location.href = localRoute('cart');
    }
  }

  function initCartDrawer() {
    doc.querySelectorAll('a[href$="/cart"], a[href*="/cart?"]').forEach((link) => link.addEventListener('click', (event) => {
      if (!cartDrawer || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      openCartDrawer();
    }));
    cartDrawer?.querySelectorAll('[data-db-cart-close]').forEach((button) => button.addEventListener('click', closeCartDrawer));
    cartDrawer?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-cart-line-change]');
      if (button) changeCartLine(Number(button.dataset.cartLineChange), Number(button.dataset.cartQuantity));
    });
    doc.querySelector('[data-db-cart-checkout]')?.addEventListener('click', () => {
      if (storeOpen) window.location.href = localRoute('checkout');
    });
    doc.querySelector('[data-db-discount-form]')?.addEventListener('submit', (event) => {
      event.preventDefault();
      const input = event.currentTarget.querySelector('[data-db-discount-input]');
      const message = event.currentTarget.querySelector('[data-db-discount-message]');
      const code = input?.value.trim();
      if (!code) {
        if (message) message.textContent = text('Vul eerst een kortingscode in.', 'Enter a discount code first.');
        return;
      }
      window.location.href = `${localRoute(`discount/${encodeURIComponent(code)}`)}?redirect=${encodeURIComponent(localRoute('cart'))}`;
    });
    doc.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && cartDrawer?.classList.contains('is-open')) closeCartDrawer();
    });
  }

  function initProductForm() {
    const form = doc.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    const quantityInput = form.querySelector('input[name="quantity"]');
    const bundleChoices = Array.from(form.querySelectorAll('[data-bundle-qty]'));
    function syncBundleChoice(quantity) {
      bundleChoices.forEach((choice) => {
        const selected = Number(choice.dataset.bundleQty) === Number(quantity);
        choice.classList.toggle('is-selected', selected);
        choice.setAttribute('aria-pressed', String(selected));
      });
    }
    bundleChoices.forEach((choice) => choice.addEventListener('click', () => {
      if (!quantityInput) return;
      const quantity = Math.max(1, Math.min(99, Number(choice.dataset.bundleQty || 1)));
      quantityInput.value = quantity;
      syncBundleChoice(quantity);
    }));
    quantityInput?.addEventListener('change', () => {
      const quantity = Math.max(1, Math.min(99, Number(quantityInput.value || 1)));
      quantityInput.value = quantity;
      syncBundleChoice(quantity);
    });
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!storeOpen) return;
      if (form.dataset.submitting === 'true') return;
      const option = form.querySelector('[data-variant-select]')?.selectedOptions[0];
      if (option && option.dataset.available !== 'true') return;
      form.dataset.submitting = 'true';
      const buttons = doc.querySelectorAll('.db-atc-button, .mobile-sticky-atc button');
      buttons.forEach((button) => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
      const error = form.querySelector('[data-product-error]');
      if (error) error.hidden = true;
      try {
        const formData = new FormData(form);
        const quantity = Math.max(1, Math.min(99, Number(formData.get('quantity') || 1)));
        formData.set('quantity', String(quantity));
        const response = await fetch(localRoute('cart/add.js'), { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body: formData });
        if (!response.ok) throw new Error('add');
        const discountCode = quantity >= 3 ? 'DB3ZAKKEN' : quantity === 2 && body.dataset.halloween !== 'true' ? 'DB2ZAKKEN' : '';
        if (discountCode) {
          window.location.href = `${localRoute(`discount/${encodeURIComponent(discountCode)}`)}?redirect=${encodeURIComponent(localRoute('cart'))}`;
          return;
        }
        openCartDrawer();
      } catch (_error) {
        if (error) {
          error.textContent = text('Toevoegen is niet gelukt. Controleer de gekozen variant en probeer opnieuw.', 'Could not add the item. Check the selected variant and try again.');
          error.hidden = false;
        }
      } finally {
        form.dataset.submitting = 'false';
        const available = form.querySelector('[data-variant-select]')?.selectedOptions[0]?.dataset.available === 'true';
        buttons.forEach((button) => { button.disabled = !available; button.removeAttribute('aria-busy'); });
      }
    });
  }

  function initCartPageQuantity() {
    doc.querySelectorAll('[data-qty-control]').forEach((control) => {
      const input = control.querySelector('input');
      control.querySelector('[data-qty-minus]')?.addEventListener('click', () => { input.value = Math.max(0, Number(input.value || 0) - 1); input.dispatchEvent(new Event('change', { bubbles: true })); });
      control.querySelector('[data-qty-plus]')?.addEventListener('click', () => { input.value = Number(input.value || 0) + 1; input.dispatchEvent(new Event('change', { bubbles: true })); });
    });
  }

  function initStoreLock() {
    if (storeOpen) return;
    doc.querySelectorAll('a[href*="/checkout"], button[name="checkout"]').forEach((element) => {
      if (element.tagName === 'BUTTON') element.disabled = true;
      element.addEventListener('click', (event) => event.preventDefault());
    });
    doc.querySelectorAll('.db-flavour-card .db-flavour-badge, .db-home-flavour-card .db-flavour-badge').forEach((badge) => { badge.textContent = launchLabel; });
  }

  initAnnouncement();
  initMobileMenu();
  initTabs();
  initImageModal();
  initLaunchPopup();
  initProductVariant();
  initCartDrawer();
  initProductForm();
  initCartPageQuantity();
  initStoreLock();
})();


/* Daily Bear mobile product layout. Desktop DOM is restored above 749px. */
(function () {
  'use strict';
  function initialise() {
    var root = document.querySelector('[data-product-root]');
    if (!root || root.dataset.dbCompactReady) return;
    root.dataset.dbCompactReady = 'true';
    var media = window.matchMedia('(max-width: 749px)');
    var undo = [], generated = [], active = false;
    var originalClass = root.className;
    var lang = document.documentElement.lang.slice(0, 2);
    var copy = ({nl:['Studies','Reviews','Resultaten & vergelijking','Productinformatie','Bekijk studie','Lees verder','Onderzoek naar creatine met training, niet naar Daily Bear gummies.','Placebo','Smaak'],fr:['Études','Avis','Résultats et comparaison','Informations produit','Voir l’étude','Lire la suite','Études sur la créatine avec entraînement, pas sur les gummies Daily Bear.','Placebo','Saveur'],de:['Studien','Bewertungen','Ergebnisse & Vergleich','Produktinformationen','Studie ansehen','Weiterlesen','Studien zu Kreatin mit Training, nicht zu Daily Bear Gummies.','Placebo','Geschmack']})[lang] || ['Studies','Reviews','Results & comparison','Product information','View study','Read more','Studies on creatine with training, not on Daily Bear gummies.','Placebo','Flavor'];
    var style = document.createElement('style');
    style.id = 'dailybear-mobile-product-layout';
    style.textContent = `
@media(max-width:749px){
.db-mobile-product.product-page{padding:14px 0 22px!important}
.db-mobile-product .product-grid{display:grid!important;grid-template-columns:minmax(0,.92fr) minmax(0,1.08fr)!important;gap:12px!important;align-items:start!important;width:calc(100% - 24px)!important;max-width:none!important;margin:0 12px!important}
.db-mobile-product .product-gallery{min-width:0!important;position:static!important;top:auto!important;max-width:100%!important}
.db-mobile-product .product-gallery__stage{border-radius:16px!important;aspect-ratio:4/5!important;min-height:0!important;height:auto!important;overflow:hidden!important}
.db-mobile-product .product-gallery__main{width:100%!important;height:100%!important;aspect-ratio:4/5!important;min-height:0!important;padding:0!important;border-radius:16px!important}
.db-mobile-product .product-gallery__main img{width:100%!important;height:100%!important;object-fit:contain!important;transform:none!important}
.db-mobile-product .product-gallery__arrow{width:36px!important;height:44px!important;min-width:0!important;font-size:24px!important;box-shadow:none!important}
.db-mobile-product .product-gallery__arrow--prev{left:3px!important}.db-mobile-product .product-gallery__arrow--next{right:3px!important}
.db-mobile-product .product-thumbs{display:grid!important;grid-template-columns:repeat(4,minmax(0,1fr))!important;gap:4px!important;margin-top:6px!important;overflow:visible!important}
.db-mobile-product .product-thumb{width:100%!important;min-width:0!important;padding:1px!important;aspect-ratio:1!important;border-radius:7px!important}.db-mobile-product .product-thumb[hidden]{display:none!important}
.db-mobile-product .product-thumb img{width:100%!important;height:100%!important;object-fit:contain!important}
.db-mobile-product .product-info{min-width:0!important;padding:0!important;position:static!important}
.db-mobile-product .stock-label{font-size:11px!important;padding:4px 8px!important;margin:0 0 5px!important}
.db-mobile-product .product-title{font-size:19px!important;line-height:1.13!important;letter-spacing:-.04em!important;margin:0 0 6px!important;overflow-wrap:anywhere}
.db-mobile-product .product-benefit-row{display:flex!important;flex-direction:column!important;gap:3px!important;margin:0 0 7px!important;font-size:11px!important;line-height:1.35!important}
.db-mobile-product .product-benefit-row span{padding:0!important;background:none!important;border:0!important;font-size:11px!important;min-height:0!important}
.db-mobile-product .product-main-price{display:flex!important;flex-wrap:wrap!important;gap:3px 7px!important;align-items:baseline!important;margin:5px 0 8px!important;padding:0!important}
.db-mobile-product .current-price{font-size:25px!important;line-height:1.1!important}.db-mobile-product .compare-price{font-size:12px!important}.db-mobile-product .db-sale-tag{display:none!important}
.db-mobile-product .db-product-form{display:flex!important;flex-direction:column!important;gap:7px!important;margin:0!important;padding:0!important}
.db-mobile-product .db-mobile-native-variant,.db-mobile-product .db-product-form:has([data-bundle-picker]) .field--quantity{display:none!important}
.db-mobile-flavors{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:5px}
.db-mobile-flavors button{padding:7px 2px;min-height:44px;min-width:0;border:1px solid #d5c8e3;border-radius:9px;background:#fff;color:#352142;font-size:11px;font-weight:700;cursor:pointer;overflow-wrap:anywhere}
.db-mobile-flavors button[aria-pressed=true]{border:2px solid #6d28d9;background:#f1e9ff;color:#522099}
.db-mobile-flavors button:focus-visible{outline:3px solid #a78bfa;outline-offset:2px}
.db-mobile-product .db-bundle-picker{display:grid!important;grid-template-columns:1fr!important;gap:5px!important;margin:0!important}
.db-mobile-product .db-bundle-picker__head{display:flex!important;flex-direction:row!important;margin:0!important;gap:0!important}.db-mobile-product .db-bundle-picker__head strong{font-size:11px!important}.db-mobile-product .db-bundle-picker__head>span{display:none!important}
.db-mobile-product .db-bundle-choice{min-height:44px!important;padding:6px 8px!important;border-radius:9px!important;gap:5px!important;box-shadow:none!important;margin:0!important}
.db-mobile-product .db-bundle-choice b{font-size:12px!important;line-height:1.2!important}.db-mobile-product .db-bundle-choice>span:first-child>small,.db-mobile-product .db-bundle-choice__price s{display:none!important}
.db-mobile-product .db-bundle-choice__price strong{font-size:13px!important}.db-mobile-product .db-bundle-choice__price small{font-size:9px!important}.db-mobile-product .db-bundle-choice:before{display:none!important}
.db-mobile-product .db-atc-button{padding:10px 5px!important;min-height:46px!important;font-size:13px!important;border-radius:11px!important;line-height:1.25!important;box-shadow:none!important;margin:0!important}
.db-mobile-product .db-payment-block{margin:0!important;padding:0!important}.db-mobile-product .db-payment-title{display:none!important}.db-mobile-product .db-payment-icons{display:flex!important;gap:3px!important;flex-wrap:wrap!important;justify-content:center!important}.db-mobile-product .db-payment-card{padding:0!important;border:0!important;background:none!important;width:28px!important;min-width:0!important;min-height:0!important}.db-mobile-product .db-payment-svg{width:28px!important;height:19px!important}
.db-mobile-product .product-trust{font-size:10px!important;line-height:1.4!important;gap:4px!important;margin:7px 0 0!important}.db-mobile-product .product-trust>*{font-size:10px!important;padding:0!important;min-height:0!important}
.db-mobile-product .db-product-shortcuts{display:grid!important;grid-template-columns:1fr!important;gap:5px!important;margin:10px 0!important;font-size:12px!important}.db-mobile-product .db-product-shortcuts a{display:flex;align-items:center;justify-content:center;min-height:44px;padding:6px;background:#f0e9f7;border-radius:9px;text-align:center}
.db-mobile-proof-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:10px;margin:18px 12px 0;align-items:start}
.db-mobile-proof-grid>section{min-width:0!important;margin:0!important;padding:12px!important;border-radius:15px!important;background:#f4eff9!important;border:1px solid #e4d9ed!important;overflow:hidden}
.db-mobile-proof-grid .page-width{width:100%!important;max-width:none!important;margin:0!important;padding:0!important}
.db-mobile-heading{margin:0 0 10px!important;font-size:17px!important;line-height:1.15!important;letter-spacing:-.03em!important;color:#352142}
.db-mobile-proof-grid .db-evidence__intro{display:none!important}
.db-mobile-proof-grid .db-evidence__stats{display:grid!important;grid-template-columns:1fr!important;gap:7px!important}
.db-mobile-proof-grid .db-evidence__stats article{padding:9px!important;border-radius:10px!important;margin:0!important;min-width:0}
.db-mobile-proof-grid .db-evidence__number{font-size:28px!important;line-height:1.1!important;color:#6d28d9!important}
.db-mobile-proof-grid .db-evidence__stats h3{font-size:12px!important;line-height:1.3!important;margin:4px 0!important}
.db-mobile-proof-grid p.db-mobile-comparison{font-size:11px!important;line-height:1.35!important;margin:4px 0!important;color:#42364e}
.db-mobile-proof-grid details{font-size:12px;line-height:1.5}.db-mobile-proof-grid summary{cursor:pointer;min-height:36px;display:flex;align-items:center;text-decoration:underline;text-underline-offset:3px;font-size:11px!important}
.db-mobile-proof-grid details p,.db-mobile-proof-grid details a{font-size:12px!important;line-height:1.5!important;overflow-wrap:anywhere}
.db-mobile-proof-grid .db-mobile-study-note{font-size:11px!important;line-height:1.5!important;margin:9px 0 0!important}
.db-mobile-proof-grid .db-reviews__header{display:block!important;margin:0 0 9px!important}.db-mobile-proof-grid .db-reviews__header>div:first-child{display:none!important}
.db-mobile-proof-grid .db-reviews__summary{display:flex!important;align-items:center!important;justify-content:flex-start!important;flex-wrap:wrap!important;gap:5px!important;padding:0!important}
.db-mobile-proof-grid .db-reviews__summary-stars svg{width:13px!important;height:13px!important}.db-mobile-proof-grid .db-reviews__summary-stars{gap:1px!important;display:flex!important}
.db-mobile-proof-grid .db-reviews__summary-meta{font-size:12px!important}.db-mobile-proof-grid .db-reviews__summary-meta strong{font-size:17px!important}
.db-mobile-proof-grid .db-review-open{min-height:36px!important;font-size:10px!important;padding:6px!important;border-radius:8px!important;width:100%!important}
.db-mobile-proof-grid .db-reviews__carousel-wrap{padding:0 0 42px!important;margin:0!important;position:relative!important}
.db-mobile-proof-grid .db-reviews__carousel{display:flex!important;gap:8px!important;align-items:flex-start!important;overflow-x:auto!important;scroll-snap-type:x mandatory!important;padding:0!important;margin:0!important;scrollbar-width:none}
.db-mobile-proof-grid .db-review-card{flex:0 0 100%!important;width:100%!important;min-width:0!important;max-width:100%!important;padding:10px!important;border-radius:10px!important;box-sizing:border-box!important;min-height:0!important;scroll-snap-align:start!important}
.db-mobile-proof-grid .db-review-card__top{display:flex!important;flex-direction:column!important;gap:5px!important;align-items:flex-start!important}.db-mobile-proof-grid .db-review-card h3{font-size:11px!important;line-height:1.35!important;margin:0!important;overflow-wrap:anywhere}
.db-mobile-proof-grid .db-review-card__verified{font-size:9px!important}.db-mobile-proof-grid .db-review-card__stars{display:flex!important;gap:1px!important}.db-mobile-proof-grid .db-review-card__stars svg{width:12px!important;height:12px!important}
.db-mobile-proof-grid .db-review-card__body p,.db-mobile-proof-grid .db-mobile-review-excerpt{font-size:12px!important;line-height:1.55!important;margin:8px 0!important;overflow-wrap:anywhere}
.db-mobile-proof-grid .db-review-card__bottom{display:none!important}
.db-mobile-proof-grid .db-reviews__arrow{position:absolute!important;top:auto!important;bottom:0!important;transform:none!important;width:36px!important;height:36px!important;min-width:0!important;box-shadow:none!important;font-size:23px!important}.db-mobile-proof-grid .db-reviews__arrow--prev{left:0!important}.db-mobile-proof-grid .db-reviews__arrow--next{right:0!important}
.db-mobile-fold{margin:12px!important;padding:12px!important;border:1px solid #e4d9ed;border-radius:13px;background:#fff;color:#352142}
.db-mobile-fold>summary{font-weight:700;min-height:24px;font-size:14px;cursor:pointer}.db-mobile-fold[open]>summary{margin-bottom:14px}
.db-mobile-fold .db-evidence__chart{margin:0!important;padding:8px!important}.db-mobile-fold h3{font-size:18px!important}.db-mobile-fold p{font-size:13px!important;line-height:1.55!important}.db-mobile-fold .db-evidence__explain{display:grid!important;grid-template-columns:1fr!important;gap:10px!important}.db-mobile-fold .db-evidence__methods{margin-top:12px!important}
.db-mobile-fold .product-short{font-size:13px!important}.db-mobile-fold .product-facts-compact{grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:6px!important}.db-mobile-fold .product-facts-compact>div{padding:8px!important}.db-mobile-fold .product-facts-compact dt{font-size:10px!important}.db-mobile-fold .product-facts-compact dd{font-size:12px!important}
body:has(.db-mobile-product) .db-header__inner{min-height:65px!important;gap:8px!important}body:has(.db-mobile-product) .db-logo img{max-width:155px!important;max-height:48px!important}
body:has(.db-mobile-product) [id*="__upsells"]>.section{padding:18px 0!important}
@media(max-width:359px){.db-mobile-product .product-grid{gap:8px!important;margin:0 8px!important;width:calc(100% - 16px)!important}.db-mobile-product .product-title{font-size:17px!important}.db-mobile-product .db-bundle-choice{padding:5px!important}.db-mobile-product .db-bundle-choice b{font-size:11px!important}.db-mobile-proof-grid{margin-inline:8px;gap:8px}.db-mobile-proof-grid>section{padding:9px!important}}
}
`;
    document.head.appendChild(style);
    function make(tag, cls, text) {
      var el = document.createElement(tag); el.className = cls || '';
      if (text) el.textContent = text;
      generated.push(el); return el;
    }
    function move(node, parent, before) {
      if (!node || !parent) return;
      var marker = document.createComment('Daily Bear desktop position');
      node.parentNode.insertBefore(marker, node);
      undo.push(function () { marker.replaceWith(node); });
      parent.insertBefore(node, before || null);
    }
    function fold(parent, label, cls) {
      var el = make('details', cls || 'db-mobile-fold');
      el.appendChild(make('summary', '', label)); parent.appendChild(el); return el;
    }
    function mount() {
      if (active) return; active = true;
      root.classList.add('db-mobile-product');
      var gallery = root.querySelector('.product-gallery');
      move(root.querySelector('.db-product-shortcuts'), gallery);
      var form = root.querySelector('#ProductForm');
      var select = form && form.querySelector('[data-variant-select]');
      if (select && select.options.length > 1) {
        var field = select.closest('.field');
        field.classList.add('db-mobile-native-variant');
        undo.push(function () { field.classList.remove('db-mobile-native-variant'); });
        var flavors = make('div','db-mobile-flavors');
        flavors.setAttribute('role','group'); flavors.setAttribute('aria-label',copy[8]);
        Array.from(select.options).forEach(function (option) {
          var button = make('button','',option.textContent.split('—')[0].trim());
          button.type='button'; button.dataset.value=option.value;
          button.addEventListener('click',function () {
            select.value=option.value;
            select.dispatchEvent(new Event('change',{bubbles:true}));
          }); flavors.appendChild(button);
        });
        function sync(){ Array.from(flavors.children).forEach(function(b){b.setAttribute('aria-pressed',String(b.dataset.value===select.value));}); }
        select.addEventListener('change',sync); sync();
        undo.push(function(){select.removeEventListener('change',sync);});
        form.insertBefore(flavors,field);
      }
      var proof = make('div','db-mobile-proof-grid'); root.appendChild(proof);
      var science = document.getElementById('CreatineResearch');
      var reviews = document.getElementById('ProductReviews');
      if (science) {
        move(science,proof);
        var container = science.querySelector('.page-width');
        container.insertBefore(make('h2','db-mobile-heading',copy[0]),container.firstChild);
        var stats = science.querySelector('.db-evidence__stats');
        Array.from(stats.children).forEach(function (article,index) {
          var comparison = make('p','db-mobile-comparison',copy[7]+': '+(index===2?'24%':'12%'));
          var details=fold(article,copy[4],'db-mobile-study');
          var paragraph=article.querySelector(':scope > p');
          var source=article.querySelector(':scope > a');
          move(paragraph,details);move(source,details);
          article.insertBefore(comparison,details);
        });
        container.appendChild(make('p','db-mobile-study-note',copy[6]));
        var results=fold(root,copy[2]);
        move(science.querySelector('.db-evidence__chart'),results);
        move(science.querySelector('.db-evidence__explain'),results);
        move(science.querySelector('.db-evidence__methods'),results);
      }
      if (reviews) {
        move(reviews,proof);
        var reviewContainer=reviews.querySelector('.page-width');
        reviewContainer.insertBefore(make('h2','db-mobile-heading',copy[1]),reviewContainer.firstChild);
        reviews.querySelectorAll('.db-review-card').forEach(function(card){
          var body=card.querySelector('.db-review-card__body');
          var text=body && body.textContent.trim();
          if(text && text.length>150){
            var excerpt=make('p','db-mobile-review-excerpt',text.slice(0,120).replace(/\s+\S*$/,'')+'…');
            card.insertBefore(excerpt,body);
            var full=fold(card,copy[5],'db-mobile-review-full');move(body,full);
          }
        });
      }
      var info=fold(root,copy[3]);
      move(root.querySelector('.product-short'),info);
      move(root.querySelector('.product-facts-compact'),info);
      move(root.querySelector('.product-accordions'),info);
      var carousel=reviews && reviews.querySelector('[data-review-carousel]');
      if(carousel) carousel.scrollLeft=0;
    }
    function restore() {
      if(!active)return;
      undo.reverse().forEach(function(fn){fn();});undo=[];
      generated.reverse().forEach(function(el){el.remove();});generated=[];
      root.className=originalClass;active=false;
    }
    function update(){if(media.matches)mount();else restore();}
    media.addEventListener('change',update); update();
  }
  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',initialise,{once:true});
  else initialise();
})();


/* Tighten the phone purchase panel without changing desktop rules. */
(function(){
  var s=document.createElement('style');
  s.textContent=`@media(max-width:749px){
  .product-page.db-mobile-product .product-grid{padding:0!important}
  .product-page.db-mobile-product .product-info .db-bundle-picker{grid-template-columns:repeat(3,minmax(0,1fr))!important;gap:4px!important}
  .product-page.db-mobile-product .product-info .db-bundle-picker__head{grid-column:1/-1}
  .product-page.db-mobile-product .product-info .db-bundle-choice{display:flex!important;flex-direction:column!important;justify-content:center!important;align-items:center!important;gap:3px!important;padding:7px 2px!important;min-height:70px!important;text-align:center!important}
  .product-page.db-mobile-product .product-info .db-bundle-choice b{font-size:10px!important}
  .product-page.db-mobile-product .product-info .db-bundle-choice__price{text-align:center!important}
  .product-page.db-mobile-product .product-info .db-bundle-choice__price strong{font-size:11px!important}
  .product-page.db-mobile-product .product-info .db-bundle-choice__price small{font-size:8px!important}
  .product-page.db-mobile-product .product-info .db-atc-button{margin:0!important;min-height:46px!important}
  .product-page.db-mobile-product .db-payment-block,.product-page.db-mobile-product .db-payment-icons{min-height:0!important;height:auto!important;margin:0!important;padding:0!important;gap:3px!important;display:flex!important;align-items:center!important}
  .product-page.db-mobile-product .db-payment-card{min-height:0!important;height:19px!important;box-shadow:none!important}
  .product-page.db-mobile-product .product-trust{display:flex!important;flex-direction:column!important;gap:4px!important}
  .product-page.db-mobile-product .product-trust__item:nth-child(n+3){display:none!important}
  .product-page.db-mobile-product .product-trust__item:before{display:none!important}
  .product-page.db-mobile-product .product-thumb{height:auto!important;min-height:36px!important;max-height:44px!important}
  }`;
  document.head.appendChild(s);
})();

(function(){var s=document.createElement("style");s.textContent="@media(max-width:749px){.db-mobile-product .db-mobile-proof-grid .db-reviews__arrow{display:flex!important;align-items:center!important;justify-content:center!important;width:40px!important;height:40px!important}.db-mobile-product .db-mobile-proof-grid .db-reviews__carousel-wrap{padding-bottom:46px!important}}";document.head.appendChild(s);})();
